import { SummaryData } from "@backend-types";
import {
  ClassExtended,
  TeacherClassDetailsCourse,
  UserClassSettings,
} from "../../interfaces";
import axios from "../../services/axios";

export type ClassMap = Map<number, ClassExtended>;

export const addClassesToMap = (
  classMap: ClassMap,
  newClasses: ClassExtended[]
): ClassMap => {
  // Create a new Map from the existing classMap
  const newMap = new Map(classMap);

  // Add the new classes to the new map
  newClasses.forEach((newClass) => {
    newMap.set(newClass.id, newClass);
  });

  // Return the new map
  return newMap;
};

export const getClassesBySchoolTeacherCourse = (
  classMap: ClassMap | ClassExtended[],
  school_id: string | null,
  teacher_id?: string | null,
  course_ids?: number[],
  archived?: boolean
): ClassExtended[] => {
  const filteredClasses: ClassExtended[] = [];

  classMap.forEach((classInfo) => {
    const isArchived =
      classInfo.end_date && new Date(classInfo.end_date).getTime() < new Date().getTime();

    if (
      `${classInfo.school_id}` === school_id &&
      (!teacher_id || classInfo.teacher_id === teacher_id) &&
      (!course_ids || course_ids.includes(classInfo.course_id ?? -1)) &&
      (archived === undefined || archived === isArchived)
    ) {
      filteredClasses.push(classInfo);
    }
  });

  return filteredClasses;
};

export const getRecentlyViewedActive = (classMap: ClassMap): ClassExtended[] => {
  const recentlyViewed: ClassExtended[] = [];

  classMap.forEach((classExtended) => {
    if (
      classExtended.settings?.viewed_at &&
      (!classExtended.end_date ||
        new Date(classExtended.end_date).getTime() >= new Date().getTime())
    ) {
      recentlyViewed.push(classExtended);
    }
  });

  return recentlyViewed.sort((a, b) => {
    const dateA =
      a.settings?.viewed_at instanceof Date
        ? a.settings.viewed_at.getTime()
        : a.settings?.viewed_at
        ? new Date(a.settings.viewed_at).getTime()
        : 0;
    const dateB =
      b.settings?.viewed_at instanceof Date
        ? b.settings.viewed_at.getTime()
        : b.settings?.viewed_at
        ? new Date(b.settings.viewed_at).getTime()
        : 0;
    return dateB - dateA; // descending order, most recent first
  });
};

export const getRecentlyViewedArchived = (classMap: ClassMap): ClassExtended[] => {
  const recentlyViewed: ClassExtended[] = [];

  classMap.forEach((classExtended) => {
    if (
      classExtended.settings?.viewed_at &&
      classExtended.end_date &&
      new Date(classExtended.end_date).getTime() < new Date().getTime()
    ) {
      recentlyViewed.push(classExtended);
    }
  });

  return recentlyViewed.sort((a, b) => {
    const dateA =
      a.settings?.viewed_at instanceof Date
        ? a.settings.viewed_at.getTime()
        : a.settings?.viewed_at
        ? new Date(a.settings.viewed_at).getTime()
        : 0;
    const dateB =
      b.settings?.viewed_at instanceof Date
        ? b.settings.viewed_at.getTime()
        : b.settings?.viewed_at
        ? new Date(b.settings.viewed_at).getTime()
        : 0;
    return dateB - dateA; // descending order, most recent first
  });
};

export const getActiveClasses = (
  classMap: ClassMap,
  teacherId?: string
): ClassExtended[] => {
  const activeClasses: ClassExtended[] = [];

  classMap.forEach((classExtended) => {
    if (
      !classExtended.end_date ||
      new Date(classExtended.end_date).getTime() >= new Date().getTime()
    ) {
      if (!teacherId) {
        activeClasses.push(classExtended);
      } else if (classExtended.teacher_id === teacherId) {
        activeClasses.push(classExtended);
      }
    }
  });

  return activeClasses;
};

export const getActiveObservedClasses = (classMap: ClassMap): ClassExtended[] => {
  const activeObservedClasses: ClassExtended[] = [];

  classMap.forEach((classExtended) => {
    if (
      classExtended.observer_ids &&
      (!classExtended.end_date ||
        new Date(classExtended.end_date).getTime() >= new Date().getTime())
    ) {
      activeObservedClasses.push(classExtended);
    }
  });
  console.log(activeObservedClasses, classMap);
  return activeObservedClasses;
};

export const getArchivedClasses = (classMap: ClassMap): ClassExtended[] => {
  const archivedClasses: ClassExtended[] = [];

  classMap.forEach((classExtended) => {
    if (
      classExtended.end_date &&
      new Date(classExtended.end_date).getTime() < new Date().getTime()
    ) {
      archivedClasses.push(classExtended);
    }
  });

  return archivedClasses;
};

export const getArchivedObservedClasses = (classMap: ClassMap): ClassExtended[] => {
  const archivedObservedClasses: ClassExtended[] = [];

  classMap.forEach((classExtended) => {
    if (
      classExtended.observer_ids &&
      classExtended.end_date &&
      new Date(classExtended.end_date).getTime() < new Date().getTime()
    ) {
      archivedObservedClasses.push(classExtended);
    }
  });

  return archivedObservedClasses;
};

export const updateClassMapWithSettings = (
  classMap: ClassMap,
  newSettings: UserClassSettings
): ClassMap => {
  const updatedClassMap = new Map(classMap); // Clone the original map

  const classExtended = updatedClassMap.get(newSettings.class_id);

  if (classExtended) {
    const updatedClassExtended = {
      ...classExtended,
      settings: {
        ...classExtended.settings,
        ...newSettings,
      },
    };

    updatedClassMap.set(newSettings.class_id, updatedClassExtended);
  }

  return updatedClassMap;
};

interface IProgressReqMap {
  classIds: number[];
  moduleIds: string[];
}

export const getClassesProgressData = async (
  classMap: ClassMap,
  courseList: TeacherClassDetailsCourse[]
): Promise<Map<number, SummaryData>> => {
  try {
    // Create a mapping of course ID to array of class IDs and module IDs
    const courseClassMap = new Map<number, IProgressReqMap>();
    Array.from(classMap.values()).forEach((classExtended) => {
      const matchedCourse = courseList.find(
        (course) => course.course_id === classExtended.course_id
      );
      if (matchedCourse) {
        if (courseClassMap.has(classExtended.course_id!)) {
          const classModuleData = courseClassMap.get(classExtended.course_id!);
          classModuleData?.classIds.push(Number(classExtended.id));
        } else {
          courseClassMap.set(classExtended.course_id!, {
            classIds: [Number(classExtended.id)],
            moduleIds: matchedCourse.course_modules,
          });
        }
      }
    });

    // Initialize a map to store the class progress data
    const classProgressDataMap = new Map<number, SummaryData>();

    // Convert Map entries to an array and then loop through each course
    const entries = Array.from(courseClassMap.entries());
    for (const [, classModuleData] of entries) {
      for (const classId of classModuleData.classIds) {
        let response = await axios.post(`/insights/class-summary`, {
          type: "summary-progress",
          classIds: [classId],
          courseFilter: {
            moduleIds: classModuleData.moduleIds,
          },
          cached: true,
        });
        // Update classProgressDataMap directly inside this loop
        response.data?.forEach((item: any) => {
          classProgressDataMap.set(Number(item.id), item as SummaryData);
        });
      }
    }

    return classProgressDataMap;
  } catch (err) {
    console.log(err);
    throw err;
  }
};
